import { theme as antdTheme } from "antd";

// Three app skins. Each one drives Ant Design through `antd`, the Google map through `mapStyle`,
// and plain CSS through the [data-app-theme] variable blocks at the top of styles.css.
// Keep the colors here in sync with those blocks.

export const THEME_STORAGE_KEY = "app-theme";
export const DEFAULT_THEME_KEY = "taxi";

const taxiColors = {
    // Black for text, focus and selection; taxi yellow only behind ink-colored content,
    // because yellow text on white is unreadable.
    selected: "#151517",
    accent: "#ffc107",
    accentHover: "#ffd23f",
    accentActive: "#e6ac00",
    accentFg: "#151517",
    accentSoft: "#fff3c2",
    accentTint: "#fffaeb",
    canvas: "#f1f1ee",
    card: "#ffffff",
    border: "#e5e3dd",
    text: "#151517",
    textSecondary: "#6c6a64",
    success: "#15803d",
    warning: "#d97706",
    error: "#dc2626",
    standMarker: "#151517",
    route: "#151517",
};

const controlColors = {
    selected: "#2dd4bf",
    accent: "#2dd4bf",
    accentHover: "#5eead4",
    accentActive: "#14b8a6",
    accentFg: "#03201c",
    accentSoft: "rgba(45, 212, 191, 0.14)",
    accentTint: "rgba(45, 212, 191, 0.07)",
    canvas: "#0a0f14",
    card: "#111a22",
    border: "#1d2a36",
    text: "#e6edf3",
    textSecondary: "#8295a8",
    success: "#34d399",
    warning: "#fbbf24",
    error: "#f87171",
    standMarker: "#a78bfa",
    route: "#2dd4bf",
};

const glassColors = {
    selected: "#0f8a7c",
    accent: "#0f8a7c",
    accentHover: "#14a493",
    accentActive: "#0b6f64",
    accentFg: "#ffffff",
    accentSoft: "rgba(15, 138, 124, 0.13)",
    accentTint: "rgba(15, 138, 124, 0.06)",
    canvas: "#f2f6f5",
    card: "rgba(255, 255, 255, 0.66)",
    border: "rgba(255, 255, 255, 0.85)",
    text: "#0f2e2a",
    textSecondary: "#557069",
    success: "#10a37f",
    warning: "#e8930c",
    error: "#e5484d",
    standMarker: "#0f2e2a",
    route: "#0f8a7c",
};

const baseMapStyle = (c) => [
    { elementType: "geometry", stylers: [{ color: c.land }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: c.label }] },
    { elementType: "labels.text.stroke", stylers: [{ color: c.land }] },
    { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
    { featureType: "poi", elementType: "geometry", stylers: [{ color: c.poi }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: c.park }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: c.road }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: c.highway }] },
    { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: c.highwayStroke }] },
    { featureType: "transit", elementType: "geometry", stylers: [{ color: c.poi }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: c.water }] },
    { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

const fontStack = (face) =>
    `'${face}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;

export const THEMES = {
    taxi: {
        key: "taxi",
        label: "Taksi žuta",
        description: "Crna i taksi žuta",
        swatch: "#ffc107",
        colors: taxiColors,
        // Driver analytics timeline. Same hues as the map pins at about two-thirds of
        // their saturation. Keys match pinColors, plus `refused`.
        timeline: {
            active: "#5fb36b",
            onDrive: "#e8735f",
            busy: "#c8463d",
            pause: "#eaa53a",
            inactive: "#cfcbc1",
            refused: "#6d5fc4",
        },
        mapStyle: baseMapStyle({
            land: "#efeee9", label: "#5f5d57", poi: "#e6e5df", park: "#dde6cf",
            road: "#ffffff", highway: "#ffe8a3", highwayStroke: "#f2c94c", water: "#c6d9e4",
        }),
        antd: {
            token: {
                colorPrimary: taxiColors.selected,
                colorInfo: taxiColors.selected,
                colorLink: taxiColors.selected,
                colorLinkHover: "#2a2a2e",
                colorSuccess: taxiColors.success,
                colorWarning: taxiColors.warning,
                colorError: taxiColors.error,
                colorBgLayout: taxiColors.canvas,
                colorBgContainer: taxiColors.card,
                colorBorder: "#d9d6ce",
                colorBorderSecondary: taxiColors.border,
                colorSplit: "#efede8",
                colorText: taxiColors.text,
                colorTextSecondary: taxiColors.textSecondary,
                colorTextHeading: taxiColors.text,
                controlOutline: "rgba(255, 193, 7, 0.35)",
                controlItemBgActive: taxiColors.accentSoft,
                controlItemBgActiveHover: "#ffeb9e",
                controlItemBgHover: "#f5f4f0",
                borderRadius: 8,
                borderRadiusLG: 12,
                borderRadiusSM: 6,
                fontFamily: fontStack("Sora"),
                boxShadow: "0 14px 34px rgba(30, 28, 20, 0.18), 0 2px 6px rgba(30, 28, 20, 0.08)",
                boxShadowSecondary: "0 14px 34px rgba(30, 28, 20, 0.18), 0 2px 6px rgba(30, 28, 20, 0.08)",
                boxShadowTertiary: "0 1px 0 rgba(0, 0, 0, 0.04), 0 6px 18px rgba(30, 28, 20, 0.07)",
            },
            components: {
                Layout: { headerBg: taxiColors.selected, footerBg: taxiColors.selected, bodyBg: taxiColors.canvas, siderBg: taxiColors.canvas },
                Button: {
                    colorPrimary: taxiColors.accent,
                    colorPrimaryHover: taxiColors.accentHover,
                    colorPrimaryActive: taxiColors.accentActive,
                    primaryColor: taxiColors.accentFg,
                    primaryShadow: "0 4px 14px rgba(255, 193, 7, 0.35)",
                    defaultShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    fontWeight: 600,
                },
                Modal: { borderRadiusLG: 16, titleFontSize: 18 },
                Menu: { itemSelectedBg: taxiColors.accentSoft, itemSelectedColor: taxiColors.selected, itemActiveBg: taxiColors.accentSoft },
                Select: { optionSelectedBg: taxiColors.accentSoft },
                Table: { headerBg: "#f7f7f4", rowSelectedBg: taxiColors.accentTint, rowSelectedHoverBg: taxiColors.accentSoft, rowHoverBg: "#faf9f6" },
                Tabs: { inkBarColor: taxiColors.selected, itemSelectedColor: taxiColors.selected, itemHoverColor: "#2a2a2e" },
                Switch: { colorPrimary: taxiColors.accentActive, colorPrimaryHover: taxiColors.accent },
                Segmented: { itemSelectedBg: taxiColors.accent, itemSelectedColor: taxiColors.accentFg },
                DatePicker: { cellActiveWithRangeBg: taxiColors.accentSoft },
                Tag: { defaultBg: "#f5f4f0" },
            },
        },
    },
    control: {
        key: "control",
        label: "Kontrolna soba",
        description: "Tamna tema za noćne smene",
        swatch: "#2dd4bf",
        colors: controlColors,
        timeline: {
            active: "#34a382",
            onDrive: "#d9675c",
            busy: "#b8423d",
            pause: "#d9a23e",
            inactive: "#2a3d4f",
            refused: "#9b8cf0",
        },
        mapStyle: baseMapStyle({
            land: "#0e161e", label: "#7f91a3", poi: "#121d27", park: "#0f221d",
            road: "#1b2733", highway: "#24384a", highwayStroke: "#2c4860", water: "#0a2434",
        }),
        antd: {
            algorithm: antdTheme.darkAlgorithm,
            token: {
                colorPrimary: controlColors.accent,
                colorInfo: controlColors.accent,
                colorLink: controlColors.accent,
                colorLinkHover: controlColors.accentHover,
                colorSuccess: controlColors.success,
                colorWarning: controlColors.warning,
                colorError: controlColors.error,
                colorBgBase: controlColors.canvas,
                colorBgLayout: controlColors.canvas,
                colorBgContainer: controlColors.card,
                colorBgElevated: "#15212c",
                colorBorder: "#24384a",
                colorBorderSecondary: controlColors.border,
                colorText: controlColors.text,
                colorTextSecondary: controlColors.textSecondary,
                colorTextHeading: controlColors.text,
                borderRadius: 8,
                borderRadiusLG: 12,
                borderRadiusSM: 6,
                fontFamily: fontStack("Manrope"),
                fontFamilyCode: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                boxShadowTertiary: "0 0 0 1px #1d2a36, 0 10px 30px rgba(0, 0, 0, 0.45)",
            },
            components: {
                Layout: { headerBg: "#0e151c", footerBg: "#0e151c", bodyBg: controlColors.canvas, siderBg: controlColors.canvas },
                Button: {
                    primaryColor: controlColors.accentFg,
                    primaryShadow: "0 0 18px rgba(45, 212, 191, 0.35)",
                    fontWeight: 600,
                },
                Modal: { borderRadiusLG: 16, titleFontSize: 18, contentBg: "#111a22", headerBg: "#111a22" },
                Table: { headerBg: "#0e161e", rowHoverBg: "#15212c" },
                Segmented: { itemSelectedBg: controlColors.accent, itemSelectedColor: controlColors.accentFg },
            },
        },
    },
    glass: {
        key: "glass",
        label: "Staklo",
        description: "Svetli prelivi i providne kartice",
        swatch: "linear-gradient(135deg, #c4efe4, #ffdcc8 55%, #cfe1fb)",
        colors: glassColors,
        timeline: {
            active: "#4fb894",
            onDrive: "#ee8a73",
            busy: "#d4545c",
            pause: "#eab14f",
            inactive: "#cddad6",
            refused: "#8a7bd8",
        },
        mapStyle: baseMapStyle({
            land: "#e7f0ee", label: "#4f6b66", poi: "#dfeae7", park: "#cfe9dc",
            road: "#ffffff", highway: "#fbe4d3", highwayStroke: "#f4cdb2", water: "#bfe0ea",
        }),
        antd: {
            token: {
                colorPrimary: glassColors.accent,
                colorInfo: glassColors.accent,
                colorLink: glassColors.accent,
                colorLinkHover: glassColors.accentHover,
                colorSuccess: glassColors.success,
                colorWarning: glassColors.warning,
                colorError: glassColors.error,
                colorBgLayout: "transparent",
                colorBgContainer: glassColors.card,
                colorBgElevated: "rgba(255, 255, 255, 0.94)",
                colorBgMask: "rgba(15, 46, 42, 0.18)",
                colorBorder: "rgba(15, 46, 42, 0.16)",
                colorBorderSecondary: glassColors.border,
                colorSplit: "rgba(15, 46, 42, 0.08)",
                colorText: glassColors.text,
                colorTextSecondary: glassColors.textSecondary,
                colorTextHeading: glassColors.text,
                controlItemBgActive: glassColors.accentSoft,
                borderRadius: 12,
                borderRadiusLG: 20,
                borderRadiusSM: 8,
                fontFamily: fontStack("DM Sans"),
                boxShadowTertiary: "0 8px 32px rgba(20, 70, 62, 0.10)",
            },
            components: {
                Layout: { headerBg: "transparent", footerBg: "transparent", bodyBg: "transparent", siderBg: "transparent" },
                Button: {
                    primaryShadow: "0 6px 18px rgba(15, 138, 124, 0.30)",
                    fontWeight: 600,
                },
                Modal: { borderRadiusLG: 22, titleFontSize: 18, contentBg: "rgba(255, 255, 255, 0.94)", headerBg: "transparent" },
                Table: { headerBg: "rgba(255, 255, 255, 0.5)", rowHoverBg: "rgba(255, 255, 255, 0.6)" },
                Segmented: { itemSelectedBg: "#ffffff" },
            },
        },
    },
};

export const THEME_OPTIONS = Object.values(THEMES);

export const getTheme = (key) => THEMES[key] ?? THEMES[DEFAULT_THEME_KEY];
